/**
 * mypage の注文詳細「返品申請進行中」 カードで顧客に提示するアクション要求を、`ActiveReturnRequestDTO`
 * (= 返品 DB の状態) から導出する pure logic。
 *
 * UI 側 (= `ReturnRequestStatusCard`) は本関数の結果を `Notice` 風のミニ枠で並べるだけにし、
 * 状態判定ロジックは UI から一切分離する (= 全分岐をこのモジュールのテストで担保する)。
 *
 * ## アクションの種類
 *
 *  - `needs_return_shipment`: 商品の返送が必要 (= 顧客が物理的に発送する作業)
 *    - 条件: `status === "WaitingForReceived"`
 *    - 注意: サバキュー側で受取確認 (= status が Received に進む) するまで本状態が続くため、
 *      入れ違い (顧客が既に発送済) のケアは UI 側で「ご返送済みの場合、弊社受取確認まで表示は
 *      変わりません」 の注意書きとして併記する
 *
 *  - `needs_bank_account`: 銀行口座入力が必要
 *    - 条件: `refundMethod === "Bank" && !hasBankAccountInfo`
 *    - 顧客マイページの BankForm に飛ばすことで実入力させる
 *
 *  - `needs_handling_fee_payment`: 事務手数料の外部決済が必要
 *    - 条件: `requiresHandlingFeeExternalPayment === true && handlingFeeExternalPaymentStatus`
 *      が `Requested` または `null`
 *    - null は「初期化前」 (= 一時的な状態) なので Requested と同じ扱いに丸める
 *
 *  - `handling_fee_pending`: 事務手数料お支払いの反映待ち
 *    - 条件: `requiresHandlingFeeExternalPayment === true && status === "OrderCreated"`
 *    - コンビニ払い等の入金確認待ち。CTA は出さず「反映中です」 のニュートラル表示に留める
 *      (= 二重決済防止)
 *
 * ## 完了した申請
 *
 * `phase === "completed"` (返金 / 交換まで終わった) では、**済んだ作業の要求だけ**を落とす
 * (返送 / 銀行口座)。返金が終わった後に「返送してください」「口座を入力してください」が
 * 残ると、済んだ手続きをもう一度求めているように読める。
 *
 * 事務手数料は落とさない。手数料の支払いと返金は別勘定で、返金が済んでも顧客が払っていない
 * 手数料は残りうる。ここで消すと請求の導線が黙って無くなる。
 *
 * ## アクションの並び順 (= UI で出す順)
 *
 * `needs_return_shipment` が最重要 (= 物理的な行動なので顧客の脳内優先度が高い) なので
 * 最上位。次に銀行口座 → 手数料決済 / 反映待ちの順。
 *
 * 排他ではない (= 複数同時に発生しうる) ので戻り値は配列。
 */

import type { ActiveReturnRequestDTO } from "./orderTypes";

export type MypageReturnAction =
  | { kind: "needs_return_shipment" }
  | { kind: "needs_bank_account" }
  | { kind: "needs_handling_fee_payment" }
  | { kind: "handling_fee_pending" };

export const computeMypageReturnActions = (
  active: ActiveReturnRequestDTO,
): MypageReturnAction[] => {
  const actions: MypageReturnAction[] = [];
  const completed = active.phase === "completed";

  if (!completed && active.status === "WaitingForReceived") {
    actions.push({ kind: "needs_return_shipment" });
  }

  if (!completed && active.refundMethod === "Bank" && !active.hasBankAccountInfo) {
    actions.push({ kind: "needs_bank_account" });
  }

  if (active.requiresHandlingFeeExternalPayment) {
    const fee = active.handlingFeeExternalPaymentStatus;
    if (fee === null || fee === "Requested") {
      actions.push({ kind: "needs_handling_fee_payment" });
    } else if (fee === "OrderCreated") {
      actions.push({ kind: "handling_fee_pending" });
    }
    // Paid なら何も追加しない
  }

  return actions;
};
